// useChatAttachment — wires a File through validation, compression, and
// Supabase Storage upload. Returns helpers the FamilyChatView can use.

import { useCallback, useState } from "react";
import { supabase, isAuthConfigured } from "./supabase";
import {
  classifyFile, compressImage, compressVideo, maxBytesFor,
  type AttachmentKind, type CompressionResult,
} from "./attachments";

const BUCKET = "chat-attachments";

export type UploadState =
  | { phase: "idle" }
  | { phase: "compressing"; kind: AttachmentKind }
  | { phase: "uploading"; kind: AttachmentKind; progress: number }
  | { phase: "done";  publicUrl: string; mime: string; size: number; kind: AttachmentKind }
  | { phase: "error"; message: string };

export function useChatAttachment(threadId: string) {
  const [state, setState] = useState<UploadState>({ phase: "idle" });

  const reset = useCallback(() => setState({ phase: "idle" }), []);

  const send = useCallback(
    async (file: File): Promise<{
      publicUrl: string; mime: string; size: number; kind: AttachmentKind;
    } | null> => {
      if (!isAuthConfigured()) {
        setState({ phase: "error", message: "尚未設定 Supabase 環境變數。" });
        return null;
      }
      if (!threadId) {
        setState({ phase: "error", message: "未選擇對話，無法上傳附件。" });
        return null;
      }

      const kind = classifyFile(file);
      const cap  = maxBytesFor(kind);

      // 1. Reject outright if the user picked a wildly-too-big file — saves
      //    us a long compression run that would still fail.
      if (file.size > cap * 2) {
        setState({
          phase: "error",
          message: `檔案太大（${(file.size / 1024 / 1024).toFixed(1)} MiB），上限為 ${(cap / 1024 / 1024).toFixed(1)} MiB。請先壓縮。`,
        });
        return null;
      }

      setState({ phase: "compressing", kind });
      let result: CompressionResult;
      try {
        if (kind === "image") {
          result = await compressImage(file, { capBytes: cap });
        } else if (kind === "video") {
          result = await compressVideo(file, { capBytes: cap });
        } else {
          // GIF — re-encoding in-browser is not reliably supported. Keep the
          // original; if it overshoots the bucket's 5 MiB cap, the upload
          // call will throw and we surface the error.
          result = { file, mime: file.type || "image/gif", size: file.size };
        }
      } catch (err) {
        setState({ phase: "error", message: (err as Error).message });
        return null;
      }

      if (result.size > cap) {
        setState({
          phase: "error",
          message: `壓縮後仍為 ${(result.size / 1024 / 1024).toFixed(2)} MiB，超過 ${(cap / 1024 / 1024).toFixed(1)} MiB 上限。請改用較小的檔案。`,
        });
        return null;
      }

      // 3. Upload to the bucket. Object name format matches the RLS policy:
      //    `split_part(name, '/', 1)::uuid = thread_id` checks the leading
      //    path segment.
      setState({ phase: "uploading", kind, progress: 0 });
      const ext = extensionFor(result.mime, file.name);
      const objectPath = `${threadId}/${Date.now()}-${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(objectPath, result.file, {
          contentType: result.mime,
          upsert: false,
        });

      if (uploadError) {
        setState({ phase: "error", message: friendlyUploadError(uploadError.message, kind) });
        return null;
      }

      // 4. Resolve the public URL. chat-attachments is private, so we hand
      //    the object path back; the message row stores it and the
      //    client resolves to a signed URL at render time.
      setState({
        phase: "done",
        publicUrl: objectPath,
        mime: result.mime,
        size: result.size,
        kind,
      });
      return {
        publicUrl: objectPath,
        mime: result.mime,
        size: result.size,
        kind,
      };
    },
    [threadId],
  );

  return { state, send, reset };
}

function extensionFor(mime: string, originalName: string): string {
  const fromName = originalName.split(".").pop();
  if (fromName && fromName.length <= 5) return fromName.toLowerCase();
  if (mime === "image/jpeg")      return "jpg";
  if (mime === "image/png")       return "png";
  if (mime === "image/webp")      return "webp";
  if (mime === "image/gif")       return "gif";
  if (mime === "video/mp4")       return "mp4";
  if (mime === "video/webm")      return "webm";
  if (mime === "video/quicktime") return "mov";
  return "bin";
}

export function friendlyUploadError(msg: string, kind: AttachmentKind): string {
  const m = msg.toLowerCase();
  if (m.includes("exceeded") || m.includes("size") || m.includes("limit")) {
    // Bug #4: previously hard-coded 5 MiB for every kind. Images cap
    // at 2 MiB; videos and GIFs cap at 5 MiB. Use the per-kind cap so
    // the message matches reality.
    const capMiB = (maxBytesFor(kind) / 1024 / 1024).toFixed(0);
    return `檔案超過 ${capMiB} MiB 上限，請先壓縮或選擇較小的檔案。`;
  }
  if (m.includes("mime") || m.includes("type"))
    return "不支援的檔案類型。";
  return `上傳失敗：${msg}`;
}
